import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import {
  GlobalBannerMessage,
  GlobalBannerMessageType
} from '../models/global-banner-interface';
import { GlobalBannerService } from '../shared/services/global-banner.service';
import { resolveSupportedLang } from '../shared/config/localized-paths';

/** Message type to CSS variant map. */
const MESSAGE_TYPE_VARIANT_MAP: Record<GlobalBannerMessageType, string> = {
  Informational: 'informational',
  Warning: 'warning',
  Alert: 'alert'
};

/** Alert > Warning > Informational. */
const MESSAGE_TYPE_PRIORITY_MAP: Record<GlobalBannerMessageType, number> = {
  Alert: 3,
  Warning: 2,
  Informational: 1
};

@Component({
  selector: 'app-global-banner',
  templateUrl: './global-banner.component.html',
  styleUrls: ['./global-banner.component.scss']
})
export class GlobalBannerComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Rendered close buttons. */
  @ViewChildren('closeButton') closeButtons!: QueryList<ElementRef<HTMLButtonElement>>;

  bannerMessages: GlobalBannerMessage[] = [];

  /** Highest-priority active banner (cached; recomputed on load/dismiss only). */
  visibleMessages: GlobalBannerMessage[] = [];

  /** Live status text for screen readers. */
  statusMessage = '';

  /** Only banners targeting this page are shown in this app. */
  private static readonly REQUIRED_PAGE = 'Public';

  private readonly destroy$ = new Subject<void>();

  /** Set on dismiss so focus moves once the next banner renders. */
  private focusPendingAfterDismiss = false;

  constructor(
    private readonly globalBannerService: GlobalBannerService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Re-fetch banners in the new language whenever the user switches EN/ES.
    this.translate.onLangChange
      .pipe(
        map((event) => event.lang),
        startWith(this.translate.currentLang),
        map((lang) => resolveSupportedLang(lang)),
        distinctUntilChanged(),
        switchMap((locale) => this.globalBannerService.getGlobalBannerMessages(locale)),
        takeUntil(this.destroy$)
      )
      .subscribe((messages) => {
        this.bannerMessages = messages.filter((message) => this.matchesFilters(message));
        this.updateVisibleMessages();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    // Move focus only after a user dismiss; never auto-focus on render.
    this.closeButtons.changes.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.focusPendingAfterDismiss) {
        return;
      }
      this.focusPendingAfterDismiss = false;
      const next = this.closeButtons.first?.nativeElement;
      if (next) {
        next.focus();
      } else {
        this.focusMainContent();
      }
    });
  }

  getVariant(message: GlobalBannerMessage): string {
    return MESSAGE_TYPE_VARIANT_MAP[message.messageType] ?? 'informational';
  }

  private getPriority(message: GlobalBannerMessage): number {
    return MESSAGE_TYPE_PRIORITY_MAP[message.messageType] ?? 0;
  }

  toggleDetails(message: GlobalBannerMessage): void {
    message.expanded = !message.expanded;
    // Announce expanded/collapsed state.
    this.statusMessage = this.translate.instant(
      message.expanded ? 'globalBanner.expanded' : 'globalBanner.collapsed'
    );
  }

  dismiss(message: GlobalBannerMessage): void {
    message.dismissed = true;
    this.updateVisibleMessages();

    if (this.visibleMessages[0]?.dismissible) {
      // Focus next banner's close button once it renders.
      this.focusPendingAfterDismiss = true;
    } else {
      this.focusMainContent();
    }
  }

  /** Recompute the single highest-priority active banner. */
  private updateVisibleMessages(): void {
    const top = this.bannerMessages
      .filter((message) => !message.dismissed)
      .reduce<GlobalBannerMessage | undefined>(
        (highest, current) =>
          !highest || this.getPriority(current) > this.getPriority(highest) ? current : highest,
        undefined
      );
    this.visibleMessages = top ? [top] : [];
  }

  private focusMainContent(): void {
    const main = document.getElementById('main-content');
    if (!main) {
      return;
    }
    if (!main.hasAttribute('tabindex')) {
      main.setAttribute('tabindex', '-1');
    }
    main.focus();
  }

  trackByPath(_index: number, message: GlobalBannerMessage): string {
    return message.path || `${message.messageType}-${_index}`;
  }

  private matchesFilters(message: GlobalBannerMessage): boolean {
    // Only show banners explicitly targeted at the Public page.
    return !!message.pages?.includes(GlobalBannerComponent.REQUIRED_PAGE);
  }
}
